/**
 * Brings a ghost block into view when it starts off screen. On a crowded
 * view the open spot for Partnr's new cards can sit outside the visible
 * canvas (the live run placed one at x = -616), so nothing visible happened
 * until the camera fit after landing. Now, when a block's first ghost
 * appears, the camera pans to it, once per block:
 *
 * - only when the first slot is not fully inside the visible canvas (left of
 *   the open Partnr panel, see visibleCanvasRect);
 * - by the smallest move that shows the block, at the same zoom, so the view
 *   slides rather than jumps somewhere new;
 * - never while the user is dragging, panning or scrolling (then it skips,
 *   and the fit after landing still applies);
 * - eased with the brand ease-out curve, or a jump with reduced motion.
 *
 * Once the block is in view, the fit after landing (bringIntoView) finds the
 * cards already visible and leaves the camera alone.
 */
import { animate } from 'framer-motion';
import { visibleCanvasRect } from '@/lib/partnrActionExecutor';
import { isUserMovingCanvas } from '@/lib/canvasInteraction';
import { useCanvasStore } from '@/stores/canvasStore';
import type { GhostBlock } from './ghosts';

/** Screen pixels kept between the block and the edge of the view: clear of the top bar and toolbars. */
const MARGIN_PX = 96;
const EASE_OUT = [0.22, 1, 0.36, 1] as const;
const PAN_SECONDS = 0.42;

type Rect = { x: number; y: number; width: number; height: number };

// Blocks already looked at; keys are tool call ids, so they never repeat
const considered = new Set<string>();
let running: { stop: () => void } | null = null;

function contains(view: Rect, r: Rect): boolean {
  return r.x >= view.x && r.y >= view.y && r.x + r.width <= view.x + view.width && r.y + r.height <= view.y + view.height;
}

/** Smallest shift of the view's start on one axis that shows [start, start + size), or `first` when it cannot all fit. */
function shiftAxis(viewStart: number, viewSize: number, start: number, size: number, first: number, edge: number): number {
  if (size > viewSize - 2 * edge) return first - edge;
  if (start < viewStart + edge) return start - edge;
  if (start + size > viewStart + viewSize - edge) return start + size - viewSize + edge;
  return viewStart;
}

/**
 * Where the view's top-left should go so the block shows with `edge` canvas
 * pixels around it, or null when its first ghost is already in view.
 */
export function revealTarget(block: Pick<GhostBlock, 'origin' | 'size' | 'slots'>, view: Rect, edge: number): { x: number; y: number } | null {
  const first = block.slots[0];
  if (!first || contains(view, first)) return null;
  // The whole reservation when it is known, else what is drawn so far
  const xs = block.slots.flatMap((s) => [s.x, s.x + s.width]);
  const ys = block.slots.flatMap((s) => [s.y, s.y + s.height]);
  const minX = Math.min(block.origin.x, ...xs);
  const minY = Math.min(block.origin.y, ...ys);
  const maxX = Math.max(block.origin.x + (block.size?.width ?? 0), ...xs);
  const maxY = Math.max(block.origin.y + (block.size?.height ?? 0), ...ys);
  return {
    x: shiftAxis(view.x, view.width, minX, maxX - minX, first.x, edge),
    y: shiftAxis(view.y, view.height, minY, maxY - minY, first.y, edge),
  };
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Looks at a block once, when it has its first ghost: pans to it if that
 * ghost is out of view and the user is not moving the canvas. Later calls
 * for the same block do nothing. Returns what it did, for tests.
 */
export function revealGhostBlock(block: GhostBlock): 'in-view' | 'panned' | 'skipped' | 'later' {
  if (block.slots.length === 0) return 'later';
  if (considered.has(block.key)) return 'skipped';
  considered.add(block.key);

  const view = visibleCanvasRect();
  const target = revealTarget(block, view, MARGIN_PX / (useCanvasStore.getState().zoom || 1));
  if (!target) return 'in-view';
  if (isUserMovingCanvas()) return 'skipped';

  // The canvas draws a point at (x + pan) * zoom, so the view starts at -pan
  const from = { x: useCanvasStore.getState().panX, y: useCanvasStore.getState().panY };
  const to = { x: -target.x, y: -target.y };
  running?.stop();
  running = null;
  if (prefersReducedMotion()) {
    useCanvasStore.setState({ panX: to.x, panY: to.y });
    return 'panned';
  }
  // onUpdate can run before animate() returns (instant animations): read the controls through a variable
  let controls: { stop: () => void } | null = null;
  let handedBack = false;
  controls = animate(0, 1, {
    duration: PAN_SECONDS,
    ease: EASE_OUT,
    onUpdate: (t) => {
      if (handedBack) return;
      // The user took the camera: stop where it is
      if (isUserMovingCanvas()) {
        handedBack = true;
        controls?.stop();
        return;
      }
      useCanvasStore.setState({ panX: from.x + (to.x - from.x) * t, panY: from.y + (to.y - from.y) * t });
    },
    onComplete: () => {
      if (running === controls) running = null;
    },
  });
  running = controls;
  return 'panned';
}

/** Test hook. */
export function resetGhostCamera(): void {
  running?.stop();
  running = null;
  considered.clear();
}
